import { useEffect, useLayoutEffect, useRef, useState } from "react";

export function normalizeManagementFilterValue(value) {
  return String(value ?? "").trim().toLowerCase();
}

export function makeManagementFilterOptions(items, getter) {
  return [...new Set(items.map((item) => String(getter(item) ?? "").trim()).filter(Boolean))]
    .sort((a, b) => a.localeCompare(b, "it", { numeric: true }))
    .map((label) => ({ label, value: normalizeManagementFilterValue(label) }));
}

export function emptyManagementRangeFilter() {
  return { from: "", to: "" };
}

function normalizedRangeNumber(value) {
  if (value === null || value === undefined || value === "") return null;
  const source = String(value)
    .replace(/\s/g, "")
    .replace(/€/g, "")
    .replace(/%/g, "");
  const normalized = (source.includes(",")
    ? source.replace(/\./g, "").replace(",", ".")
    : source
  ).replace(/[^0-9.+-]/g, "");
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
}

function normalizedRangeDate(value) {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value.getTime();
  const source = String(value).trim();
  const italian = source.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
  const parsed = italian
    ? new Date(Number(italian[3]), Number(italian[2]) - 1, Number(italian[1])).getTime()
    : new Date(source).getTime();
  return Number.isNaN(parsed) ? null : parsed;
}

function normalizedRangeTime(value) {
  if (!value) return null;
  const match = String(value).match(/(\d{1,2}):(\d{2})/);
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return null;
  return (hours * 60) + minutes;
}

// Ricerca diretta: più termini separati da virgole; almeno un termine deve corrispondere.
export function formatManagementSearchInput(value) {
  return String(value ?? "");
}

export function matchesManagementSearch(values, search) {
  const terms = String(search ?? "").split(",").map((term) => term.trim().toLocaleLowerCase("it")).filter(Boolean);
  if (!terms.length) return true;
  const candidates = (Array.isArray(values) ? values : [values]).map((value) => String(value ?? "").toLocaleLowerCase("it"));
  return terms.some((term) => candidates.some((value) => value.includes(term)));
}

export function matchesManagementFilter(value, filter = [], filterType = "checks") {
  if (filterType === "text") {
    const token = normalizeManagementFilterValue(filter);
    return !token || normalizeManagementFilterValue(value).includes(token);
  }

  if (filterType !== "checks" && Array.isArray(value)) {
    if (!value.length) return false;
    return value.some((entry) => matchesManagementFilter(entry, filter, filterType));
  }

  if (filterType === "checks") {
    return !Array.isArray(filter) || !filter.length || filter.includes(normalizeManagementFilterValue(value));
  }

  const range = filter && typeof filter === "object" && !Array.isArray(filter) ? filter : emptyManagementRangeFilter();
  if (!range.from && !range.to) return true;

  let current = null;
  let from = null;
  let to = null;

  if (filterType === "number") {
    current = normalizedRangeNumber(value);
    from = normalizedRangeNumber(range.from);
    to = normalizedRangeNumber(range.to);
  } else if (filterType === "date") {
    current = normalizedRangeDate(value);
    from = normalizedRangeDate(range.from);
    to = normalizedRangeDate(range.to);
    if (to !== null) to += (24 * 60 * 60 * 1000) - 1;
  } else if (filterType === "time") {
    current = normalizedRangeTime(value);
    from = normalizedRangeTime(range.from);
    to = normalizedRangeTime(range.to);
  }

  if (current === null) return false;
  if (from !== null && current < from) return false;
  if (to !== null && current > to) return false;
  return true;
}

export function compareManagementValues(left, right, type = "text") {
  if (type === "number") {
    const a = normalizedRangeNumber(left);
    const b = normalizedRangeNumber(right);
    if (a === null && b === null) return 0;
    if (a === null) return 1;
    if (b === null) return -1;
    return a - b;
  }

  if (type === "date") {
    const a = normalizedRangeDate(left);
    const b = normalizedRangeDate(right);
    if (a === null && b === null) return 0;
    if (a === null) return 1;
    if (b === null) return -1;
    return a - b;
  }

  if (type === "time") {
    const a = normalizedRangeTime(left);
    const b = normalizedRangeTime(right);
    if (a === null && b === null) return 0;
    if (a === null) return 1;
    if (b === null) return -1;
    return a - b;
  }

  return String(left ?? "").localeCompare(String(right ?? ""), "it", {
    sensitivity: "base",
    numeric: true,
  });
}

export function sortManagementRows(rows = [], sort = {}, getters = {}) {
  const key = String(sort?.key || "");
  if (!key || !getters[key]) return Array.isArray(rows) ? [...rows] : [];
  const direction = sort?.direction === "desc" ? -1 : 1;
  const definition = getters[key];
  const getter = typeof definition === "function" ? definition : definition.getter;
  const type = typeof definition === "function" ? "text" : (definition.type || "text");

  return [...rows].sort((a, b) => {
    const result = compareManagementValues(getter(a), getter(b), type);
    if (result !== 0) return result * direction;
    return String(a?._id || "").localeCompare(String(b?._id || ""));
  });
}

function toColumnCssVar(columnKey) {
  return `--management-col-${String(columnKey || "").replace(/[^a-zA-Z0-9_-]/g, "-")}`;
}

function getColumnMinWidth(label, minWidth) {
  const explicit = Number(minWidth || 0);
  if (Number.isFinite(explicit) && explicit > 0) return explicit;
  return Math.max(136, Math.ceil(String(label || "").length * 9.5) + 104);
}

function getRenderedColumnMinWidth(headerCell, fallbackWidth) {
  if (!headerCell || typeof window === "undefined") return fallbackWidth;

  const labelElement = headerCell.querySelector(".management_filter_label, .management_plain_header_label");
  const filterButton = headerCell.querySelector(".management_filter_button");
  const sortButton = headerCell.querySelector(".management_sort_button");
  const sortIcon = headerCell.querySelector(".management_sort_indicator");
  const styles = window.getComputedStyle(headerCell);
  const sortStyles = sortButton ? window.getComputedStyle(sortButton) : null;
  const horizontalPadding = (parseFloat(styles.paddingLeft) || 0) + (parseFloat(styles.paddingRight) || 0);
  const headerGap = parseFloat(styles.columnGap || styles.gap) || 0;
  const sortGap = sortStyles ? (parseFloat(sortStyles.columnGap || sortStyles.gap) || 0) : 0;
  const sortPadding = sortStyles
    ? (parseFloat(sortStyles.paddingLeft) || 0) + (parseFloat(sortStyles.paddingRight) || 0)
    : 0;
  const labelWidth = labelElement ? Math.ceil(labelElement.scrollWidth) : 0;
  const filterWidth = filterButton ? Math.ceil(filterButton.getBoundingClientRect().width) : 0;
  const sortWidth = sortIcon ? Math.ceil(sortIcon.getBoundingClientRect().width) : 0;
  const contentWidth = labelWidth
    + filterWidth
    + sortWidth
    + horizontalPadding
    + sortPadding
    + (filterWidth && sortButton ? headerGap : 0)
    + (sortWidth && labelWidth ? sortGap : 0)
    + 18;

  return Math.max(fallbackWidth, Math.ceil(contentWidth));
}

export function ManagementColumnResizeHandle({ columnKey, label, minWidth }) {
  const handleRef = useRef(null);
  const minimumWidth = getColumnMinWidth(label || columnKey, minWidth);

  useLayoutEffect(() => {
    const handle = handleRef.current;
    if (!handle || !columnKey) return undefined;

    const shell = handle.closest(".management_table_shell");
    const headerCell = handle.closest("[data-management-resize-key]");
    if (!shell || !headerCell) return undefined;

    const renderedMinimumWidth = getRenderedColumnMinWidth(headerCell, minimumWidth);
    const currentWidth = Math.round(headerCell.getBoundingClientRect().width);
    if (currentWidth < renderedMinimumWidth) shell.style.setProperty(toColumnCssVar(columnKey), `${renderedMinimumWidth}px`);

    handle.dataset.minWidth = String(renderedMinimumWidth);
    return undefined;
  }, [columnKey, minimumWidth]);

  function startResize(event) {
    if (event.button !== 0) return;
    event.preventDefault();
    event.stopPropagation();

    const shell = event.currentTarget.closest(".management_table_shell");
    const headerCell = event.currentTarget.closest("[data-management-resize-key]");
    if (!shell || !headerCell || !columnKey) return;

    const renderedMinimumWidth = getRenderedColumnMinWidth(headerCell, minimumWidth);
    const startX = event.clientX;
    const startWidth = Math.max(renderedMinimumWidth, Math.round(headerCell.getBoundingClientRect().width));
    const cssVarName = toColumnCssVar(columnKey);

    document.body.classList.add("is_management_column_resizing");

    function moveColumn(pointerEvent) {
      const nextWidth = Math.max(renderedMinimumWidth, Math.round(startWidth + pointerEvent.clientX - startX));
      shell.style.setProperty(cssVarName, `${nextWidth}px`);
    }

    function stopResize() {
      document.body.classList.remove("is_management_column_resizing");
      window.removeEventListener("pointermove", moveColumn);
      window.removeEventListener("pointerup", stopResize);
      window.removeEventListener("pointercancel", stopResize);
    }

    window.addEventListener("pointermove", moveColumn);
    window.addEventListener("pointerup", stopResize, { once: true });
    window.addEventListener("pointercancel", stopResize, { once: true });
  }

  return <span ref={handleRef} className="management_column_resize_handle" data-column-key={columnKey} data-min-width={minimumWidth} onPointerDown={startResize} aria-hidden="true" />;
}

export function ManagementListLoading({ label = "Caricamento dati" }) {
  return (
    <div className="management_table_visible_status management_loading_status" role="status" aria-live="polite">
      <span className="management_loading_spinner" aria-hidden="true"><span /></span>
      <span>{label}</span>
    </div>
  );
}

export default function ManagementFilterHeader({
  columnKey,
  label,
  options = [],
  value = [],
  activeFilter,
  onToggle,
  onCheckChange,
  onClear,
  onRangeChange,
  filterType = "checks",
  filterable = true,
  sortable = true,
  sortDirection = "",
  onSort,
  resizable = true,
  minWidth,
}) {
  const isOpen = filterable && activeFilter === columnKey;
  const isRange = ["date", "time", "number"].includes(filterType);
  const isText = filterType === "text";
  const appliedRange = isRange && value && typeof value === "object" && !Array.isArray(value) ? value : emptyManagementRangeFilter();
  const appliedText = isText && typeof value === "string" ? value : "";
  const [draftRange, setDraftRange] = useState(appliedRange);
  const [draftText, setDraftText] = useState(appliedText);
  const isActive = isRange
    ? Boolean(appliedRange.from || appliedRange.to)
    : isText
      ? Boolean(appliedText.trim())
      : Array.isArray(value) && value.length > 0;

  useEffect(() => {
    if (isOpen && isRange) setDraftRange(appliedRange);
    if (isOpen && isText) setDraftText(appliedText);
  }, [isOpen, isRange, isText, appliedRange.from, appliedRange.to, appliedText]);

  function clearRange() {
    const empty = emptyManagementRangeFilter();
    setDraftRange(empty);
    onRangeChange?.(columnKey, empty);
    onClear?.(columnKey);
  }

  function confirmRange() {
    onRangeChange?.(columnKey, draftRange);
    onToggle?.(columnKey);
  }

  function clearText() {
    setDraftText("");
    onRangeChange?.(columnKey, "");
    onClear?.(columnKey);
  }

  function confirmText() {
    onRangeChange?.(columnKey, draftText);
    onToggle?.(columnKey);
  }

  const rangeType = filterType === "number" ? "number" : filterType;
  const rangeLabels = filterType === "date"
    ? ["Da data", "A data"]
    : filterType === "time"
      ? ["Da ora", "A ora"]
      : ["Da valore", "A valore"];

  function handleHeaderClick(event) {
    if (!sortable) return;
    if (event.target.closest(".management_filter_button, .management_filter_popover, .management_column_resize_handle, .management_sort_button")) return;
    onSort?.(columnKey);
  }

  return (
    <div className={`management_filter_header${isOpen ? " is_open" : ""}${sortDirection ? " is_sorted" : ""}`} role="columnheader" data-management-resize-key={columnKey} aria-sort={sortDirection === "asc" ? "ascending" : sortDirection === "desc" ? "descending" : "none"} onClick={handleHeaderClick}>
      {filterable && (
        <button type="button" className={`management_filter_button${isActive ? " is_active" : ""}`} onClick={() => onToggle?.(columnKey)} aria-label={`Filtra ${label}`} aria-expanded={isOpen}>
          <svg className="management_filter_icon" viewBox="0 0 24 24" aria-hidden="true"><path d="M4 5h16l-6.2 7.1v5.4l-3.6 1.8v-7.2L4 5Z" /></svg>
        </button>
      )}
      <button type="button" className={`management_sort_button${sortable ? "" : " is_disabled"}`} onClick={() => sortable && onSort?.(columnKey)} disabled={!sortable} aria-label={sortable ? `Ordina ${label}${sortDirection === "asc" ? " in modo decrescente" : " in modo crescente"}` : label}>
        <span className="management_filter_label">{label}</span>
        {sortable && <span className={`management_sort_indicator${sortDirection ? ` is_${sortDirection}` : ""}`} aria-hidden="true"><svg viewBox="0 0 24 24" focusable="false"><path className="management_sort_bars" d="M5.25 7h5M5.25 12h3.75M5.25 17h2.5" /><path className="management_sort_arrow" d="M16.25 5.25v12.5m-3.4-3.4 3.4 3.4 3.4-3.4" /></svg></span>}
      </button>
      {resizable && <ManagementColumnResizeHandle columnKey={columnKey} label={label} minWidth={minWidth} />}
      {isOpen && (
        <div className="management_filter_popover">
          {isRange ? (
            <div className="management_filter_range_fields">
              <label><span>{rangeLabels[0]}</span><input type={rangeType} step={filterType === "number" ? "any" : undefined} value={draftRange.from || ""} onChange={(event) => setDraftRange((current) => ({ ...current, from: event.target.value }))} autoFocus /></label>
              <label><span>{rangeLabels[1]}</span><input type={rangeType} step={filterType === "number" ? "any" : undefined} value={draftRange.to || ""} onChange={(event) => setDraftRange((current) => ({ ...current, to: event.target.value }))} /></label>
              <div className="management_filter_actions"><button type="button" className="management_filter_clear" onClick={clearRange}>Azzera</button><button type="button" className="management_filter_apply" onClick={confirmRange}>Conferma</button></div>
            </div>
          ) : isText ? (
            <div className="management_filter_range_fields">
              <label><span>Contiene</span><input type="text" value={draftText} onChange={(event) => setDraftText(event.target.value)} onKeyDown={(event) => { if (event.key === "Enter") confirmText(); }} autoFocus /></label>
              <div className="management_filter_actions"><button type="button" className="management_filter_clear" onClick={clearText}>Azzera</button><button type="button" className="management_filter_apply" onClick={confirmText}>Conferma</button></div>
            </div>
          ) : (
            <>
              <div className="management_filter_checks">
                {options.length === 0 && <div className="management_filter_empty">Nessun valore</div>}
                {options.map((option) => {
                  const checked = Array.isArray(value) && value.includes(option.value);
                  return <label className="management_filter_check" key={option.value}><input type="checkbox" checked={checked} onChange={(event) => onCheckChange?.(columnKey, option.value, event.target.checked)} /><span>{option.label}</span></label>;
                })}
              </div>
              <button type="button" className="management_filter_clear" onClick={() => onClear?.(columnKey)}>Azzera filtro</button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
